import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody, CardHeader } from '../components/Card.js'
import { Section } from '../components/Section.js'
import { StatusPill } from '../components/StatusPill.js'
import { Icon } from '../components/Icon.js'

export const Route = createFileRoute('/_app/children/$id')({
  component: ChildDetailPage,
})

interface ChildRow {
  studentId: string
  fullName: string
  examBoard: string | null
  schoolName: string | null
  className: string | null
  academyId: string
  academyName: string
  academySlug: string
  relationship: string | null
  isPrimary: boolean
}

interface AttendanceResponse {
  summary: { present: number; absent: number; late: number; excused: number; total: number }
  rows: {
    id: string
    date: string
    status: 'present' | 'absent' | 'late' | 'excused'
    notes: string | null
  }[]
}

interface PerformanceRow {
  scoreId: string
  marks: number
  notes: string | null
  testId: string
  testName: string
  testType: string
  subject: string | null
  maxMarks: number
  testDate: string
}

interface ChildExamsResponse {
  studentId: string
  studentName: string
  examBoard: string | null
  academyId: string
  academyName: string
  exams: {
    id: string
    board: string
    subject: string
    examDate: string
    notes: string | null
    daysUntil: number
  }[]
}

function ChildDetailPage() {
  const { id } = Route.useParams()

  const children = useQuery({
    queryKey: ['me', 'children'],
    queryFn: async () => {
      const res = await api<ChildRow[]>('/api/v1/me/children')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const attendance = useQuery({
    queryKey: ['me', 'attendance', id],
    queryFn: async () => {
      const res = await api<AttendanceResponse>(
        `/api/v1/me/attendance?studentId=${id}`,
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const performance = useQuery({
    queryKey: ['me', 'performance', id],
    queryFn: async () => {
      const res = await api<PerformanceRow[]>(
        `/api/v1/me/performance?studentId=${id}`,
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const upcomingExams = useQuery({
    queryKey: ['me', 'exams', id],
    queryFn: async () => {
      const res = await api<ChildExamsResponse[]>(
        `/api/v1/me/exams?studentId=${id}&days=90`,
      )
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const child = children.data?.find((c) => c.studentId === id)

  if (children.isLoading) {
    return <p className="text-sm text-fg-subtle">Loading…</p>
  }
  if (!child) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-danger">
          We couldn&apos;t find this child linked to your account.
        </p>
        <Link to="/children" className="text-sm text-[var(--color-primary)]">
          ← Back to children
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-4xl space-y-8">
      <PageHeader
        title={child.fullName}
        description={
          <>
            <Link to="/children" className="hover:text-fg">
              ← All children
            </Link>
            {' · '}
            {child.academyName}
            {child.className ? ` · ${child.className}` : ''}
          </>
        }
      />

      <Card>
        <CardHeader title="Profile" />
        <CardBody>
          <dl className="grid grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <DLRow label="Class" value={child.className ?? '—'} />
            <DLRow label="School" value={child.schoolName ?? '—'} />
            <DLRow
              label="Exam board"
              value={
                child.examBoard
                  ? child.examBoard.toUpperCase().replace('_', ' ')
                  : '—'
              }
            />
            <DLRow label="Relationship" value={child.relationship ?? '—'} />
          </dl>
        </CardBody>
      </Card>

      {/* Upcoming exams */}
      {upcomingExams.data?.[0]?.exams && upcomingExams.data[0].exams.length > 0 ? (
        <Section
          title="Upcoming exams"
          description={`Next 90 days · ${upcomingExams.data[0].examBoard?.toUpperCase().replace('_', ' ') ?? ''}`}
        >
          <Card>
            <ul className="divide-y divide-border">
              {upcomingExams.data[0].exams.map((e) => {
                const urgency =
                  e.daysUntil <= 3
                    ? 'bg-danger-tint text-danger border-danger-border'
                    : e.daysUntil <= 14
                      ? 'bg-warning-tint text-warning border-warning-border'
                      : 'bg-surface-subtle text-fg-muted border-border'
                return (
                  <li key={e.id} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-fg">{e.subject}</p>
                        <p className="text-xs text-fg-subtle inline-flex items-center gap-1.5">
                          <Icon name="calendar" size={12} />
                          {new Date(e.examDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </p>
                        {e.notes ? (
                          <p className="mt-1 text-xs text-fg-muted whitespace-pre-wrap">
                            {e.notes}
                          </p>
                        ) : null}
                      </div>
                      <span
                        className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium ${urgency}`}
                      >
                        {e.daysUntil <= 0
                          ? 'Today'
                          : e.daysUntil === 1
                            ? 'Tomorrow'
                            : `In ${e.daysUntil} days`}
                      </span>
                    </div>
                  </li>
                )
              })}
            </ul>
          </Card>
        </Section>
      ) : null}

      {/* Attendance summary */}
      <Section title="Attendance" description="Last 30 days of records.">
        {attendance.data ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardBody>
                <p className="text-xs text-fg-subtle">Present</p>
                <p className="mt-1 text-2xl font-semibold text-success">
                  {attendance.data.summary.present}
                </p>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <p className="text-xs text-fg-subtle">Absent</p>
                <p className="mt-1 text-2xl font-semibold text-danger">
                  {attendance.data.summary.absent}
                </p>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <p className="text-xs text-fg-subtle">Late</p>
                <p className="mt-1 text-2xl font-semibold text-warning">
                  {attendance.data.summary.late}
                </p>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <p className="text-xs text-fg-subtle">Total marked</p>
                <p className="mt-1 text-2xl font-semibold text-fg">
                  {attendance.data.summary.total}
                </p>
              </CardBody>
            </Card>
          </div>
        ) : null}

        {attendance.data && attendance.data.rows.length > 0 ? (
          <Card>
            <ul className="divide-y divide-border">
              {attendance.data.rows.slice(0, 12).map((row) => (
                <li
                  key={row.id}
                  className="px-5 py-2.5 flex items-center justify-between"
                >
                  <span className="text-sm text-fg-muted font-mono">
                    {new Date(row.date).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                    })}
                  </span>
                  <div className="flex items-center gap-3">
                    {row.notes ? (
                      <span className="text-xs text-fg-subtle italic">
                        {row.notes}
                      </span>
                    ) : null}
                    <StatusPill tone={row.status}>{row.status}</StatusPill>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <Card>
            <CardBody className="text-center py-6 text-sm text-fg-muted">
              No attendance recorded yet.
            </CardBody>
          </Card>
        )}
      </Section>

      {/* Performance */}
      <Section title="Test scores" description="Most recent first.">
        {performance.data && performance.data.length > 0 ? (
          <Card>
            <ul className="divide-y divide-border">
              {performance.data.map((row) => {
                const pct = Math.round((row.marks / row.maxMarks) * 100)
                return (
                  <li key={row.scoreId} className="px-5 py-3">
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-fg">
                          {row.testName}
                          {row.subject ? (
                            <span className="text-fg-subtle ml-1">· {row.subject}</span>
                          ) : null}
                        </p>
                        <p className="text-xs text-fg-subtle">
                          {row.testType.replace('_', ' ')} ·{' '}
                          {new Date(row.testDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-base font-semibold text-fg">
                          {row.marks}
                          <span className="text-fg-subtle font-normal">
                            /{row.maxMarks}
                          </span>
                        </p>
                        <p
                          className={`text-xs ${
                            pct >= 75
                              ? 'text-success'
                              : pct >= 50
                                ? 'text-warning'
                                : 'text-danger'
                          }`}
                        >
                          {pct}%
                        </p>
                      </div>
                    </div>
                    {row.notes ? (
                      <p className="mt-1 text-xs text-fg-muted italic">{row.notes}</p>
                    ) : null}
                  </li>
                )
              })}
            </ul>
          </Card>
        ) : (
          <Card>
            <CardBody className="text-center py-6 text-sm text-fg-muted">
              No test scores yet.
            </CardBody>
          </Card>
        )}
      </Section>
    </div>
  )
}

function DLRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="col-span-2 text-fg">{value}</dd>
    </>
  )
}
